import { useEffect, useState } from "react";

// DEPENDENCIES
import * as Yup from "yup";
import * as Sentry from "@sentry/react";
import { saveAs } from "file-saver";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";

// UTILS
import {
  genericToastifyError,
  hasJointSignature,
  isToros,
  isWina,
  isLandToken,
  isMabra,
} from "../../utils";
import {
  onUpdateCompletedForm,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateCompletedForm as onUpdateCompletedFormSecurities,
  onUpdateStep as onUpdateStepSecurities,
} from "../../reducers/securitiesReducer";
import { onUpdatePreconfirmation } from "../../reducers/preconfirmationReducer";
import useWindowWidth from "../../hooks/useWindowWidth";
import { logAnalyticsAndHotjarEvent } from "../../main";
import useTemplateInterpreter from "../../hooks/useTemplateInterpreter";

// TYPES
import { Preconfirmation } from "../../types";
import { OnboardingType } from "../../types";

// CONNECTORS
import { completeDraft } from "../../connectors/connectors";

// COMPONENTS
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import { Checkbox } from "@almafintech/react-components/Checkbox";
import { Button } from "@almafintech/react-components/Button";
import PreconfirmationCard from "../PreconfirmationCard/PreconfirmationCard";
import Layout from "../Layout/Layout";

// ICONS
import padlock from "../../assets/images/icons/ui/padlock-grey.svg";

// STYLES
import styles from "./PreconfirmationPage.module.scss";

const PreconfirmationPage = () => {
  const {
    container,
    center,
    checkboxesGroup,
    checkboxWrapper,
    checkboxLabel,
    optionalText,
    optionalBox,
    buttonsGroup,
    button,
    text,
  } = styles;

  const [isDisabled, setIsDisabled] = useState(true);

  const [searchParams] = useSearchParams();
  const templateURL = searchParams.get("template");

  const state = useAppSelector((state) => state);
  const { steps } = state.activeTemplate;

  // Fall back to template param, in case something went wrong and state reset.
  // We can't do default value on destructure it's `null` when unset, not `undefined`.
  let { onboardingType } = state.activeTemplate;
  onboardingType = onboardingType ?? (templateURL as OnboardingType) ?? null;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [preconfirmationData, setPreconfirmationData] =
    useState<Preconfirmation | null>(null);

  const fieldsInitialValues =
    preconfirmationData?.checkboxes?.reduce(
      (acc: Record<string, boolean>, { name, initialValue }) => {
        acc[name] = initialValue;
        return acc;
      },
      {}
    ) || {};

  const fieldsValidations = preconfirmationData?.checkboxes?.reduce(
    (acc: Record<string, Yup.Schema>, { name, validation }) => {
      acc[name] = validation;
      return acc;
    },
    {}
  );

  const formik = useFormik({
    initialValues: fieldsInitialValues,
    validationSchema: Yup.object().shape({ ...fieldsValidations }),
    onSubmit: async (values) => {
      try {
        if (preconfirmationData?.onSubmit)
          return preconfirmationData.onSubmit({
            values,
            state,
            dispatch,
            navigate,
          });

        if (preconfirmationData?.sendTermsAndConditions)
          await preconfirmationData.sendTermsAndConditions(state, values);

        await completeDraft({ draftUUID });

        dispatch(
          onboardingType === "SECURITIES"
            ? onUpdateCompletedFormSecurities(true)
            : onUpdateCompletedForm(true)
        );
        dispatch({ type: "reset-store" });
        resetForm();
        logAnalyticsAndHotjarEvent("onboarding_completed", {
          personType: onboardingType,
        });
        navigate(successPage);
      } catch (error) {
        console.log(error);
        Sentry.captureException(error);
        showToastMessage(genericToastifyError, {
          containerId: "layoutPage",
          type: "error",
        });
      }
    },
  });

  const {
    getFieldProps,
    values,
    handleSubmit,
    isSubmitting,
    setValues,
    resetForm,
  } = formik;

  const { loadTemplate, urlTemplate, draftUUID, formValues } = useTemplateInterpreter({
    dispatch,
    onboardingType,
    state,
  });

  const previousStep = steps && steps?.length - 1;

  const handleBack = () => {
    dispatch(onUpdatePreconfirmation(true));
    navigate(urlTemplate);
    dispatch(
      onboardingType === "SECURITIES"
        ? onUpdateStepSecurities(Number(previousStep))
        : onUpdateStep(Number(previousStep))
    );
  };

  const linksData = preconfirmationData?.checkboxes?.flatMap(
    ({ links }) => links || []
  );
  const linksElements = linksData?.map(({ id }) => document.getElementById(id));

  const handleOnClick = (
    e: React.MouseEvent<HTMLInputElement>,
    name: string
  ) => {
    e.preventDefault();

    const target = e.target as Node;
    const label = document.getElementById(`label-${name}`);

    // Find if user clicked on a link to terms or notes
    const linkClicked = linksElements?.find((link) => link?.contains(target));

    if (linkClicked) {
      // Find the link data to download the file
      const linkData = linksData?.find(({ id }) => id === linkClicked.id);
      saveAs(linkData?.url || "", `${linkData?.name}.pdf`);
    } else if (!label?.contains(target)) {
      toggleCheckbox(name);
    }
  };

  const toggleCheckbox = (name: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      [name]: !prevValues[name],
    }));
  };

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const successSearchParams = new URLSearchParams({
    landToken: isLandToken(formValues) ? "true" : "",
    toros: isToros(formValues) ? "true" : "",
    wina: isWina(formValues) ? "true" : "",
    mabra: isMabra(formValues) ? "true" : "",
    allariaSecurities: onboardingType === "SECURITIES" ? "true" : "",
    signatories: hasJointSignature(formValues) ? "true" : "",
    language: state.securities.language ?? "es",
  });

  const successPage = `/apertura-exitosa${
    successSearchParams.toString() ? `?${successSearchParams.toString()}` : ""
  }`;

  useEffect(() => {
    if (!state.preconfirmation.isFromNormalFlow) {
      loadTemplate();
    }
  }, [onboardingType]);

  useEffect(() => {
    const { preconfirmation } = state.activeTemplate;

    if (preconfirmation && formValues) {
      const preconfirmationData = preconfirmation({
        values: formValues || {},
      });
      setPreconfirmationData(preconfirmationData);
    }
  }, [state.activeTemplate, formValues]);

  useEffect(() => {
    if (values.termsAndConditions) {
      logAnalyticsAndHotjarEvent("terms_accepted");
    }
  }, [values]);

  useEffect(() => {
    // When the user reaches the "preconfirmation" without having gone through the flow, we redirect them
    if (!draftUUID) {
      const obTypeToNavigate = urlTemplate;
      if (!obTypeToNavigate.includes("firmante")) {
        navigate(urlTemplate);
      } else navigate(`/empresarial`);
    }
  }, [preconfirmationData]);

  useEffect(() => {
    if (preconfirmationData) {
      if (
        !preconfirmationData?.checkboxes?.length ||
        !preconfirmationData?.sendTermsAndConditions
      ) {
        setIsDisabled(false);
      } else {
        const requiredFieldsAreEmpty = preconfirmationData.checkboxes.some(
          ({ name, required }) => required && !values[name]
        );
        setIsDisabled(requiredFieldsAreEmpty);
      }
    }
  }, [preconfirmationData, values]);

  return (
    <Layout hideLogo={!isMobile} hideStepper className={container}>
      <PreconfirmationCard
        preconfirmationSteps={preconfirmationData?.steps || []}
      />
      <form className={checkboxesGroup}>
        {preconfirmationData?.checkboxes?.map(({ name, label, required }) => (
          <div className="flex gap-2" key={name}>
            <Checkbox
              {...getFieldProps(name)}
              isSelected={values[name]}
              classNames={{
                wrapper: checkboxWrapper,
              }}
              onClick={(e) => handleOnClick(e, name)}
            >
              <div className={checkboxLabel} id={`label-${name}`}>
                {label}
                {!required && isMobile && (
                  <p className={optionalText}>(opcional)</p>
                )}
              </div>
            </Checkbox>
            {!required && !isMobile && <p className={optionalBox}>Opcional</p>}
          </div>
        ))}
      </form>

      <div className={center}>
        <div className={buttonsGroup}>
          <Button
            text="Volver"
            variant="secondary"
            type="button"
            onClick={handleBack}
            className={button}
          />
          <Button
            text={
              preconfirmationData?.submitButtonText
                ? preconfirmationData.submitButtonText({ state, formValues })
                : "Abrir cuenta"
            }
            variant="primary"
            type="submit"
            onClick={() => handleSubmit()}
            isLoading={isSubmitting}
            disabled={isDisabled || isSubmitting}
          />
        </div>
        {!isMobile && (
          <div className={text}>
            <img src={padlock} alt="candado" />
            <span>
              Todos tus datos son guardados con seguridad y no serán compartidos
            </span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PreconfirmationPage;
